/**
 * Group Mapper Functions
 *
 * API ↔ Domain ↔ View 간 변환 로직
 */

import type {
  GroupListItemApiDto,
  GroupDetailApiDto,
  GroupWithDetailsApiDto,
  CreateGroupApiDto,
  AssignSchoolsApiDto,
  ReleaseSchoolsApiDto,
} from '../api/group.api'

import type {
  Group,
  GroupDetail,
  GroupItem,
  CreateGroupCommand,
  AssignSchoolsCommand,
  ReleaseSchoolsCommand,
} from '../domain/group'

import { toDomainSchoolFromGroup } from './school.mapper'
import { toDomainTank } from './tank.mapper'

// ============================================
// API → Domain
// ============================================

/**
 * API Group List Item → Domain Group
 */
export function toDomainGroup(apiGroup: GroupListItemApiDto): Group {
  return {
    id: apiGroup.id,
    name: apiGroup.name,
    createdAt: undefined, // API에 없음
    updatedAt: undefined, // API에 없음
  }
}

/**
 * API Group List → Domain Group 배열
 */
export function toDomainGroups(apiGroups: GroupListItemApiDto[]): Group[] {
  return apiGroups.map(toDomainGroup)
}

/**
 * API Group with Details → Domain GroupDetail
 */
export function toDomainGroupDetail(apiGroup: GroupWithDetailsApiDto): GroupDetail {
  const baseGroup = toDomainGroup(apiGroup)

  // list가 올바른 구조인지 확인
  const list = apiGroup.list as any

  const items: GroupItem[] = Array.isArray(list)
    ? list.map((item: any) => ({
        school: toDomainSchoolFromGroup(item.school),
        tank: toDomainTank(item.tank),
      }))
    : []

  return {
    ...baseGroup,
    items,
  }
}

/**
 * API Groups with Details → Domain GroupDetail 배열
 */
export function toDomainGroupDetails(apiGroups: GroupWithDetailsApiDto[]): GroupDetail[] {
  return apiGroups.map(toDomainGroupDetail)
}

// ============================================
// Domain → API (Commands)
// ============================================

/**
 * Domain CreateGroupCommand → API Request
 */
export function toApiCreateGroup(command: CreateGroupCommand): CreateGroupApiDto {
  return {
    name: command.name,
  }
}

/**
 * Domain AssignSchoolsCommand → API Request
 * Note: group_id is passed as path parameter, not in body
 */
export function toApiAssignSchools(command: AssignSchoolsCommand): AssignSchoolsApiDto {
  return {
    school_ids: command.schoolIds,
    reason: command.reason,
  }
}

/**
 * Domain ReleaseSchoolsCommand → API Request
 * Note: group_id is passed as path parameter, not in body
 */
export function toApiReleaseSchools(command: ReleaseSchoolsCommand): ReleaseSchoolsApiDto {
  return {
    school_ids: command.schoolIds,
    reason: command.reason,
  }
}
